import { PencilIcon } from "@heroicons/react/outline";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityIcon } from "@/components/ui/icons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { getEntitySingularLabel } from "@/components/UnifiedRegistry/entity-labels";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { useEditFolder, useEditPromptTemplate, useRenameReport } from "@/queries";
import { useEditDatasetGroup } from "@/queries/datasets/useEditDatasetGroup";
import { patchFavoriteDisplayName } from "@/queries/favorites";
import {
  Entity,
  EntityType,
  getEntityNumericId,
  getRequiredEntityNumericId,
} from "@/queries/folder-entities/types";
import { useUpdateInputVariableSet } from "@/queries/input-variable-sets";
import { useUpdateSkillCollection } from "@/queries/skill-collections";
import { useUpdateSmartTable } from "@/queries/smart-tables/useUpdateSmartTable";
import { useUpdateToolRegistry } from "@/queries/tool-registry";
import { useUpdateWorkflowName } from "@/queries/workflows";
import { displayToast } from "@/utils/toast";

const RENAMEABLE_ENTITY_TYPES: string[] = [
  "FOLDER",
  "PROMPT",
  "SNIPPET",
  "WORKFLOW",
  "REPORT",
  "DATASET",
  "INPUT_VARIABLE_SET",
  "SKILL_COLLECTION",
  "TOOL",
  "SMART_TABLE",
];

interface RenameEntityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entity?: Entity | null;
  onSuccess?: () => void;
}

export const RenameEntityDialog = ({
  open,
  onOpenChange,
  entity,
  onSuccess,
}: RenameEntityDialogProps) => {
  const [entityName, setEntityName] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const authContext = useAuth();
  const userContext = useUser();
  const queryClient = useQueryClient();
  const { userToken } = authContext!;
  const activeWorkspaceId = userContext.activeWorkspaceId!;

  const { workspaceId } = useParams();

  const updateFolderMutation = useEditFolder(userToken!);
  const editPromptTemplateMutation = useEditPromptTemplate();
  const updateWorkflowNameMutation = useUpdateWorkflowName();
  const editDatasetGroupMutation = useEditDatasetGroup(userToken!, entity?.folder_id ?? null);
  const updateInputVariableSetMutation = useUpdateInputVariableSet(userToken!);
  const updateSkillCollectionMutation = useUpdateSkillCollection();
  const updateToolMutation = useUpdateToolRegistry();
  const updateSmartTableMutation = useUpdateSmartTable();

  const renameReportMutation = useRenameReport(
    userToken!,
    getEntityNumericId(entity?.id) ?? 0,
    entity?.folder_id ?? null,
  );

  useEffect(() => {
    let animationFrameId: number | undefined;

    if (open && entity) {
      setEntityName(entity.name || "");
      setIsRenaming(false);

      animationFrameId = window.requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    } else {
      inputRef.current?.blur();
    }

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [open, entity]);

  const registryItem = entity ? entity : null;
  const entityTypeLabel = entity ? getEntitySingularLabel(entity.type) : "Item";

  const handleRename = async () => {
    if (!entityName.trim()) {
      displayToast("Please enter a name", ToastType.error);
      return;
    }

    if (!entity) {
      displayToast("Invalid item selected", ToastType.error);
      return;
    }

    if (entityName.trim() === entity.name) {
      onOpenChange(false);
      return;
    }

    setIsRenaming(true);

    try {
      if (entity.type === "FOLDER") {
        await updateFolderMutation.mutateAsync({
          folder_id: getRequiredEntityNumericId(entity.id),
          name: entityName.trim(),
        });
        displayToast("Folder renamed successfully", ToastType.success);
      } else if (entity.type === "PROMPT" || entity.type === "SNIPPET") {
        await editPromptTemplateMutation.mutateAsync({
          userToken: userToken!,
          prompt_id: getRequiredEntityNumericId(entity.id),
          activeWorkspaceId,
          basePromptTemplate: {
            prompt_name: entityName.trim(),
            folder_id: entity.folder_id,
          },
        });
        displayToast(`${entityTypeLabel} renamed successfully`, ToastType.success);
      } else if (entity.type === "WORKFLOW") {
        await updateWorkflowNameMutation.mutateAsync({
          id: entity.id.toString(),
          name: entityName.trim(),
          folder_id: entity.folder_id ?? null,
        });
        displayToast("Workflow renamed successfully", ToastType.success);
      } else if (entity.type === "REPORT") {
        await renameReportMutation.mutateAsync(entityName.trim());
        displayToast("Evaluation renamed successfully", ToastType.success);
      } else if (entity.type === "DATASET") {
        await editDatasetGroupMutation.mutateAsync({
          datasetGroupId: getRequiredEntityNumericId(entity.id),
          name: entityName.trim(),
        });
        displayToast("Dataset renamed successfully", ToastType.success);
      } else if (entity.type === "INPUT_VARIABLE_SET") {
        await updateInputVariableSetMutation.mutateAsync({
          id: getRequiredEntityNumericId(entity.id),
          data: { name: entityName.trim() },
        });
        displayToast("Input variable set renamed successfully", ToastType.success);
      } else if (entity.type === "SKILL_COLLECTION") {
        await updateSkillCollectionMutation.mutateAsync({
          workspaceId: Number(workspaceId ?? activeWorkspaceId),
          collectionId: String(entity.id),
          name: entityName.trim(),
        });
        displayToast("Skill collection renamed successfully", ToastType.success);
      } else if (entity.type === "TOOL") {
        await updateToolMutation.mutateAsync({
          workspaceId: Number(workspaceId ?? activeWorkspaceId),
          toolId: getRequiredEntityNumericId(entity.id),
          name: entityName.trim(),
        });
        displayToast("Tool renamed successfully", ToastType.success);
      } else if (entity.type === "SMART_TABLE") {
        await updateSmartTableMutation
          .mutateAsync({ id: String(entity.id), title: entityName.trim() })
          .catch(error => {
            displayToast("Failed to rename table", ToastType.error);
            throw error;
          });
        queryClient.invalidateQueries({ queryKey: ["folder-entities"] });
        displayToast("Table renamed successfully", ToastType.success);
      } else {
        displayToast(
          `Renaming ${entityTypeLabel.toLowerCase()}s is not yet implemented`,
          ToastType.error,
        );
        onOpenChange(false);
        return;
      }

      patchFavoriteDisplayName(queryClient, {
        workspaceId: Number(workspaceId ?? activeWorkspaceId),
        entityType: entity.type as EntityType,
        entityId: entity.id,
        name: entityName.trim(),
      });

      onOpenChange(false);
      onSuccess?.();
    } catch {
      // Error toast is already displayed by the mutation's onError handler
    } finally {
      setIsRenaming(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleRename();
    }
  };

  const handleCancel = () => {
    inputRef.current?.blur();
    onOpenChange(false);
  };

  const handleDialogPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!inputRef.current) {
      return;
    }

    const { target } = e;
    if (target instanceof Node && !inputRef.current.contains(target)) {
      inputRef.current.blur();
    }
  };

  const isNameChanged = entity ? entityName.trim() !== entity.name : false;
  const canRename = entityName.trim() && isNameChanged;

  return (
    <Dialog open={open && !!entity} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md overflow-hidden" onPointerDown={handleDialogPointerDown}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-medium">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
              <PencilIcon className="h-4 w-4 text-blue-600" />
            </div>
            <span className="truncate">Rename {entityTypeLabel}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-hidden">
          {entity && (
            <div className="flex items-center gap-3 overflow-hidden rounded-lg bg-gray-50 p-3">
              <div className="flex-shrink-0">
                {registryItem && <EntityIcon type={registryItem.type} />}
              </div>
              <div className="min-w-0 flex-1 overflow-hidden">
                <OverflowDisplayTooltip
                  content={entity.name}
                  className="block w-full text-sm font-medium text-gray-900"
                  styles={{ display: "block", width: "100%" }}
                >
                  {entity.name}
                </OverflowDisplayTooltip>
                <p className="text-xs text-gray-500">{entityTypeLabel}</p>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="entity-name">New Name</Label>
            <Input
              id="entity-name"
              type="text"
              ref={inputRef}
              value={entityName}
              onChange={e => setEntityName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Enter ${entityTypeLabel.toLowerCase()} name...`}
              className="w-full"
              disabled={isRenaming}
            />
          </div>

          {!isNameChanged && entityName.trim() && (
            <div className="rounded-md bg-gray-50 p-3">
              <p className="text-sm text-gray-600">
                The {entityTypeLabel.toLowerCase()} name hasn't changed.
              </p>
            </div>
          )}

          {entity && !RENAMEABLE_ENTITY_TYPES.includes(entity.type) && (
            <div className="rounded-md bg-yellow-50 p-3">
              <p className="text-sm text-yellow-800">
                Renaming {entityTypeLabel.toLowerCase()}s is not yet available. This feature is
                coming soon.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <Button variant="outline" onClick={handleCancel} disabled={isRenaming}>
            Cancel
          </Button>
          <Button
            onClick={handleRename}
            disabled={Boolean(
              !canRename ||
                isRenaming ||
                (entity && !RENAMEABLE_ENTITY_TYPES.includes(entity.type)),
            )}
            className="min-w-[100px]"
          >
            {isRenaming ? (
              <span className="flex items-center gap-2">
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Renaming...
              </span>
            ) : (
              `Rename ${entityTypeLabel}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RenameEntityDialog;
